import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  useEffect(() => {
    if (currentCategory) {
      document.title = `${currentCategory.name} - 免费91网页`;
    } else {
      document.title = '全部分类 - 免费91网页';
    }
  }, [currentCategory]);

  const filteredArticles = useMemo(() => {
    if (activeCategory === 'all') return articles;
    return articles.filter((a) => a.categorySlug === activeCategory);
  }, [activeCategory]);

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    ...(currentCategory && currentCategory.slug !== 'all'
      ? [{ name: currentCategory.slug }]
      : [{ name: 'all' }]),
  ];

  const categoryName = currentCategory?.slug === 'all' || !currentCategory ? '全部' : currentCategory.name;

  return (
    <main className="pt-20">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-8">
        <div className="mb-6">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
            {categoryName}
          </h1>
          <p className="text-sm text-foreground-600">
            共 {filteredArticles.length} 篇文章
          </p>
        </div>

        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap flex items-center gap-1.5 ${
                activeCategory === cat.slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              <i className={`${cat.icon} text-xs`}></i>
              {cat.name}
            </Link>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {filteredArticles.map((article) => (
            <Link
              key={article.id}
              to={`/article/${article.slug}`}
              className="group block rounded-lg overflow-hidden border border-background-200/70 bg-background-50 hover:border-primary-200 transition-all duration-300"
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3">
                  <span className="px-2.5 py-1 bg-primary-500/90 text-background-50 text-xs font-medium rounded-md whitespace-nowrap">
                    {article.category}
                  </span>
                </div>
              </div>
              <div className="p-4">
                <h2 className="text-base font-semibold font-heading text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                  {article.title}
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                  {article.excerpt}
                </p>
                <div className="flex items-center gap-4 text-xs text-foreground-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {article.readTime} 分钟阅读
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filteredArticles.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
              <i className="ri-inbox-line text-2xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-600">该分类下暂无文章</p>
          </div>
        )}
      </div>
    </main>
  );
}